import moment from 'moment';
import { ConfigConstants } from '../../../config/config-constants';
import {
    TIMERECORDING_MEANS_OF_TRANSPORT_CODES,
    TIMERECORDING_TYPE_OF_WORK_CODES,
} from '../../../models/complex/time-recording-entry.model';
import { ODataTimesheetAndExpensesOutModel } from '../../../models/complex/timesheet-and-expenses-p58.model';
import { ODataTimesheetEntryOutModel } from '../../../models/complex/timesheet-entry.model';
import {
    addressesById,
    AddressState,
    outboundAddrNumber,
    TimesheetAddressState,
    toTimesheetAddress,
} from '../../address';
import { TimesheetEntryState, TimesheetState } from '../timesheet-collection.state';

/**
 * Contextual inputs the transform needs that do not live in the serialized timesheet slice.
 * Kept a thin, explicit input bag so the store can feed it without threading services into
 * the pure function.
 *
 * - `frontId` — the user's front id; the top-level `FrontId` of the out model and the key of
 *   the synthetic "additional expenses" notification (`frontId:'':''`).
 * - `displayTimesheetAddressOptions` — config flag gating the start/end address (SOW relabel,
 *   last-TRBK address overwrite) and the "first TRTO" additional-distance target.
 * - `country` — the country the address `Country` field is overridden with (user master data).
 * - `displayMeansOfTransport` — config flag gating the PASSENGER work-code transform.
 * - `addresses` — the address collection an entry's stored address id is resolved against; a
 *   dangling id (an address deleted after the entry was recorded) resolves to no address.
 */
export interface TimesheetOutModelContext {
    frontId: string;
    displayTimesheetAddressOptions: boolean;
    country: string;
    displayMeansOfTransport: boolean;
    addresses: readonly AddressState[];
}

/**
 * Marker for the synthetic start-of-work / end-of-work entries and the real (notification or
 * manual) entries. Only the real entries carry a work code, address, distance and notification.
 */
type NormalizedEntryKind = 'sow' | 'eow' | 'normal' | 'manual';

interface NormalizedEntry {
    kind: NormalizedEntryKind;
    start: moment.Moment;
    end: moment.Moment;
    /** Raw work code before any transform (undefined for SOW/EOW markers). */
    workCode?: string;
    meansOfTransport?: string;
    afterHourCallOut: boolean;
    travelType?: TIMERECORDING_TYPE_OF_WORK_CODES;
    /** `undefined` means "no Distance field" (no distance model existed); '' still emits `Distance: ''`. */
    distance?: string;
    address?: TimesheetAddressState;
    addressInOutModel: boolean;
    notificationFrontId: string;
    notificationQmNum: string;
    notificationMaNum: string;
    /** SOW/EOW only — the echoed selected start/end address radio value. */
    addrNumber?: string;
}

/** The legacy relabels the surviving SOW marker with this literal work code. */
const WORK_CODE_START_OF_WORK = 'SOW';

// --- Shared helpers ----------------------------------------------------------

function formatOdataDate(value: moment.Moment): string | null {
    return value.isValid() ? value.format(ConfigConstants.formatDateNoTimezone) : null;
}

function formatOdataTime(value: moment.Moment): string | null {
    return value.isValid() ? value.format(ConfigConstants.formatXSDTimeDuration) : null;
}

// --- Normalization -----------------------------------------------------------

function normalizeEntry(entry: TimesheetEntryState, addresses: ReadonlyMap<string, AddressState>): NormalizedEntry {
    const resolvedAddress = entry.address ? addresses.get(entry.address) : undefined;
    const shared = {
        start: entry.start ? moment(entry.start) : moment.invalid(),
        end: entry.end ? moment(entry.end) : moment.invalid(),
        // A dangling id (its address deleted after the entry was recorded) resolves to no address.
        address: resolvedAddress ? toTimesheetAddress(resolvedAddress) : undefined,
        // `addressInOutModel` defaults to true (the model constructor default).
        addressInOutModel: entry.addressInOutModel ?? true,
        notificationFrontId: entry.notificationId?.frontId ?? '',
        notificationQmNum: entry.notificationId?.qmNum ?? '',
        notificationMaNum: entry.notificationId?.maNum ?? '',
    };

    if (entry.kind === 'manual') {
        return {
            ...shared,
            kind: 'manual',
            // The manual entry's work code is the selected gap task type.
            workCode: entry.taskType ?? '',
            afterHourCallOut: false,
            travelType: entry.travelType,
            // Manual entries never carry a distance out of the box; only the additional
            // distance placement can add one.
            distance: undefined,
        };
    }

    return {
        ...shared,
        kind: 'normal',
        workCode: entry.workCode ?? '',
        meansOfTransport: entry.meansOfTransport,
        afterHourCallOut: entry.afterHourCallOut ?? false,
        distance: entry.distance > 0 ? entry.distance.toString() : undefined,
    };
}

/**
 * The entries that go into a submission, in slice order.
 *
 * Work entries only count once their confirmation has been submitted. A timesheet holding an
 * in-progress confirmation cannot be submitted at all (the submit gate rejects it), so this
 * filter should never actually drop anything — it stays as the belt-and-braces that keeps an
 * in-progress entry out of a submission if that gate is ever bypassed.
 *
 * @param current the current timesheet slice
 * @param addresses the address collection an entry's stored address id is resolved against
 * @returns the submittable entries, normalized, in chronological order
 */
function collectSubmittableEntries(
    current: TimesheetState,
    addresses: ReadonlyMap<string, AddressState>,
): NormalizedEntry[] {
    return (current.entries ?? [])
        .filter((entry) => entry.kind !== 'work' || entry.isConfirmationSubmitted)
        .map((entry) => normalizeEntry(entry, addresses));
}

function buildSowMarker(startOfWork: moment.Moment, startAddressValue: string): NormalizedEntry {
    // SOW/EOW markers set all four datetimes to the same instant.
    return {
        kind: 'sow',
        start: startOfWork,
        end: startOfWork,
        afterHourCallOut: false,
        addressInOutModel: false,
        notificationFrontId: '',
        notificationQmNum: '',
        notificationMaNum: '',
        addrNumber: startAddressValue,
    };
}

function buildEowMarker(endOfWork: moment.Moment, endAddressValue: string): NormalizedEntry {
    return {
        kind: 'eow',
        start: endOfWork,
        end: endOfWork,
        afterHourCallOut: false,
        addressInOutModel: false,
        notificationFrontId: '',
        notificationQmNum: '',
        notificationMaNum: '',
        addrNumber: endAddressValue,
    };
}

// --- Ordering ----------------------------------------------------------------
//
// There is no sort here: the slice keeps `entries` in chronological order as a reducer
// invariant (see `withSortedEntries`), so filtering and mapping preserves it.

function lastEntryDateTime(current: TimesheetState, middleEntries: NormalizedEntry[]): moment.Moment {
    if (middleEntries.length > 0) {
        return middleEntries[middleEntries.length - 1].end.clone();
    }
    return current.startOfWork ? moment(current.startOfWork).clone() : moment.invalid();
}

/**
 * Port of `TimesheetModel.getEndOfWork` for a non-submitted (current) timesheet: the later of
 * the last entry's end time and the recorded end of work; a fixed "now" fallback when neither
 * exists.
 *
 * @param current the current timesheet slice
 * @param middleEntries the sorted notification + manual entries
 * @returns the end-of-work instant used for the EOW marker
 */
function computeEndOfWork(current: TimesheetState, middleEntries: NormalizedEntry[]): moment.Moment {
    const lastEntry = lastEntryDateTime(current, middleEntries);
    const endOfWork = current.endOfWork ? moment(current.endOfWork) : moment.invalid();

    if (lastEntry.isValid() && (!endOfWork.isValid() || lastEntry.isAfter(endOfWork))) {
        return lastEntry.clone();
    } else if (!endOfWork.isValid()) {
        return moment().startOf('minute');
    }
    return endOfWork.clone();
}

// --- Business rules ----------------------------------------------------------

/**
 * Port of the start/end address rules. When enabled and there is at least one aligned TRTO and
 * TRBK entry besides the SOW/EOW markers, the start address is sent (relabelling the SOW) and
 * the end address overwrites the last TRBK entry's address.
 *
 * @param flatEntries the flat [SOW, …middle, EOW] entry list (mutated in place)
 * @param ctx contextual inputs
 * @param startOfWork the start-of-work instant (the SOW marker's end time)
 * @param endOfWork the end-of-work instant (the EOW marker's start time)
 * @returns whether the start address should be sent (SOW marker kept + relabelled)
 */
function applyAddressRules(
    flatEntries: NormalizedEntry[],
    ctx: TimesheetOutModelContext,
    startOfWork: moment.Moment,
    endOfWork: moment.Moment,
): boolean {
    const minLengthOfTimesheetEntriesToDisplayAddressOptions = 4;
    if (
        !ctx.displayTimesheetAddressOptions ||
        flatEntries.length <= minLengthOfTimesheetEntriesToDisplayAddressOptions
    ) {
        return false;
    }

    const firstEntry = flatEntries[1];
    const sendStartAddress =
        firstEntry.kind === 'normal' &&
        firstEntry.workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO &&
        firstEntry.start.isSame(startOfWork);

    const lastMarker = flatEntries[flatEntries.length - 1];
    const lastEntry = flatEntries[flatEntries.length - 2];
    const sendEndAddress =
        lastEntry.kind === 'normal' &&
        lastEntry.workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK &&
        lastEntry.end.isSame(endOfWork);

    if (sendEndAddress) {
        // Overwrite the last TRBK entry's address with a blank address carrying only the
        // end address radio value (the country is overridden later from ctx.country).
        lastEntry.address = {
            street: '',
            city: '',
            postCode: '',
            country: '',
            addrText: '',
            addrNumber: lastMarker.addrNumber ?? '',
            allowToHide: false,
            id: '',
        };
    }

    return sendStartAddress;
}

// The legacy CONCUR additional-distance placement is not ported: it read the distance off the
// synthetic "additional expenses" notification, which the slice cannot hold — a notification owning
// no entries has nowhere to live now that the confirmation fields ride on the entries. Nothing in
// the store ever fed it either, since additional expenses are not part of the slice. When the
// expenses domain is migrated it brings its own field and its own placement rule.

// --- Mapping -----------------------------------------------------------------

function resolveWorkCode(entry: NormalizedEntry, ctx: TimesheetOutModelContext): string {
    let workCode = entry.workCode ?? '';

    // Manual travel entries force their work code to travel-back / travel-back-standby.
    if (entry.kind === 'manual' && entry.travelType) {
        return entry.travelType === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            ? TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            : TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
    }

    if (ctx.displayMeansOfTransport && entry.meansOfTransport === TIMERECORDING_MEANS_OF_TRANSPORT_CODES.PASSENGER) {
        if (workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
            workCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRTU;
        }
        if (workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
            workCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRBU;
        }
    }

    if (workCode === TIMERECORDING_TYPE_OF_WORK_CODES.OSR && entry.afterHourCallOut) {
        workCode = TIMERECORDING_TYPE_OF_WORK_CODES.OSR_AFTER_HOURS_CALL_OUT;
    }

    return workCode;
}

function mapAddress(address: TimesheetAddressState, ctx: TimesheetOutModelContext): Record<string, string> {
    // A selected US state is appended to the city as "<city>;<state>".
    const city = address.stateOfUSA ? `${address.city ?? ''};${address.stateOfUSA}` : (address.city ?? '');
    return {
        Street: address.street ?? '',
        City: city,
        PostalCode: address.postCode ?? '',
        // The country is always overridden from the user master data.
        Country: ctx.country,
        AddrNotes: address.addrText ?? '',
        // a manual number is this device's own bookkeeping for an address the backend does not know
        AddrNumber: outboundAddrNumber(address.addrNumber),
    };
}

function mapEntry(entry: NormalizedEntry, ctx: TimesheetOutModelContext): ODataTimesheetEntryOutModel {
    const outModel: Record<string, string | null> = {};

    if (entry.kind === 'sow' || entry.kind === 'eow') {
        // Markers only carry the address radio value (when address options are shown); they are
        // sliced off unless the SOW survives, in which case it is relabelled afterwards.
        if (ctx.displayTimesheetAddressOptions) {
            outModel.AddrNumber = outboundAddrNumber(entry.addrNumber) ?? '';
        }
        return outModel as unknown as ODataTimesheetEntryOutModel;
    }

    outModel.StartDate = formatOdataDate(entry.start);
    outModel.StartTime = formatOdataTime(entry.start);
    outModel.EndDate = formatOdataDate(entry.end);
    outModel.EndTime = formatOdataTime(entry.end);
    outModel.WorkCode = resolveWorkCode(entry, ctx);

    if (entry.distance !== undefined) {
        outModel.Distance = entry.distance;
    }

    if (entry.addressInOutModel) {
        if (!entry.address) {
            if (entry.kind === 'normal') {
                throw new Error(`No address set in timesheet entry`);
            }
        } else {
            Object.assign(outModel, mapAddress(entry.address, ctx));
        }
    }

    // Notification identifier fields (FrontId is intentionally not part of the entry out model).
    outModel.NotificationNo = entry.notificationQmNum;
    outModel.TaskNumber = entry.notificationMaNum;

    return outModel as unknown as ODataTimesheetEntryOutModel;
}

/**
 * What a submit sends and settles: the out model, and the end of work it was built with — the EOW
 * marker's instant, which the slice stores on the submitted timesheet so that what is kept is what
 * was sent.
 */
export interface TimesheetSubmission {
    outModel: ODataTimesheetAndExpensesOutModel;
    endOfWork: string;
}

/**
 * Like {@link buildTimesheetOutModel}, but also returns the end of work resolved for the EOW
 * marker, so the caller does not have to derive it a second time.
 *
 * @param current the current timesheet slice being submitted
 * @param ctx contextual inputs not contained in the slice
 * @returns the out model and the end of work it was built with, as an ISO timestamp
 */
export function buildTimesheetSubmission(current: TimesheetState, ctx: TimesheetOutModelContext): TimesheetSubmission {
    const startOfWork = current.startOfWork ? moment(current.startOfWork) : moment.invalid();

    const middleEntries = collectSubmittableEntries(current, addressesById(ctx.addresses));
    const endOfWork = computeEndOfWork(current, middleEntries);

    // The flat list only exists when the timesheet has a valid start of work.
    const flatEntries: NormalizedEntry[] = [];
    if (startOfWork.isValid()) {
        flatEntries.push(buildSowMarker(startOfWork, current.startAddressValue ?? ''));
        flatEntries.push(...middleEntries);
        flatEntries.push(buildEowMarker(endOfWork, current.endAddressValue ?? ''));
    }

    const sendStartAddress = applyAddressRules(flatEntries, ctx, startOfWork, endOfWork);

    const activitySet = flatEntries.map((entry) => mapEntry(entry, ctx));
    if (sendStartAddress && activitySet.length > 0) {
        activitySet[0].WorkCode = WORK_CODE_START_OF_WORK;
    }
    // Drop the EOW marker (always) and the SOW marker (unless it carries the start address).
    const slicedActivitySet = activitySet.slice(sendStartAddress ? 0 : 1, -1);

    return {
        outModel: {
            FrontId: ctx.frontId,
            ActivitySet: slicedActivitySet,
            CseExpenseSet: [],
        },
        endOfWork: endOfWork.toISOString(),
    };
}

/**
 * Build the SAP submit out model directly from the serializable timesheet slice, without
 * constructing the `TimesheetAndExpensesP58Model` form-model hierarchy.
 *
 * Replicates `TimesheetAndExpensesP58Model.getOutModel()`: it assembles the flat, ordered
 * activity list ([SOW, …sorted notification+manual entries, EOW]), applies the address business
 * rules and slices off the SOW/EOW markers. Expenses are always empty.
 *
 * @param current the current timesheet slice being submitted
 * @param ctx contextual inputs not contained in the slice
 * @returns the `{ FrontId, ActivitySet, CseExpenseSet }` out model posted to the backend
 */
export function buildTimesheetOutModel(
    current: TimesheetState,
    ctx: TimesheetOutModelContext,
): ODataTimesheetAndExpensesOutModel {
    return buildTimesheetSubmission(current, ctx).outModel;
}
